/* eslint-disable no-use-before-define */
import { FC, PropsWithChildren } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Breadcrumbs, Button, Group, Loader, Token, Typography } from '@screentone/core';

import styles from './contentParameterSettings.module.scss';
import { useMappingContext } from './MappingContext';

interface LoadingContentParametersProps {}

export const LoadingContentParameters: FC<PropsWithChildren<LoadingContentParametersProps>> = ({ children }) => (
  <div className={styles.loaderView}>
    <Loader size="lg" />
    <Typography
      margin={{
        top: 'md'
      }}
      variant="bodytext"
    >
      {children}
    </Typography>
  </div>
);

LoadingContentParameters.displayName = 'LoadingContentParameters';

interface ContentParametersErrorProps {
  error: unknown;
}

export const ContentParametersError: FC<PropsWithChildren<ContentParametersErrorProps>> = ({ error }) => {
  let errorText = <Typography variant="bodytext">Error loading section list page</Typography>;
  if (error instanceof Error) {
    errorText = <Typography variant="bodytext">Error loading section list page: {error.message}</Typography>;
  }

  return (
    <>
      <Typography variant="header2">Error</Typography>
      {errorText}
    </>
  );
};

ContentParametersError.displayName = 'ContentParametersError';

interface ContentParametersNavigationControlsProps {
  artifactName: string;
  allessehId: string;
}

export const ContentParametersNavigationControls: FC<ContentParametersNavigationControlsProps> = ({
  artifactName,
  allessehId
}) => {
  const { property } = useParams();

  return (
    <Group margin={{ bottom: 'lg' }} valign="center">
      <Breadcrumbs>
        <Breadcrumbs.Item
          componentEl={Link}
          to={`/${property}/site-config/mobile-and-app`}
          className={styles.propertyBreadcrumb}
        >
          {property}
        </Breadcrumbs.Item>
        <Breadcrumbs.Item componentEl={Link} to={`/${property}/site-config/mobile-and-app/content-presentation/list`}>
          Section List
        </Breadcrumbs.Item>
        <Breadcrumbs.Item componentEl={Link} to={`/${property}/site-config/mobile-and-app/content-presentation/list/${allessehId}`}>
          {artifactName}
        </Breadcrumbs.Item>
      </Breadcrumbs>
    </Group>
  );
};

ContentParametersNavigationControls.displayName = 'ContentParametersNavigationControls';

interface AccordionTitleWithUndoProps {
  mappingIsModified: boolean;
  title?: React.ReactNode;
  onUndo?: () => void;
}

export const AccordionTitleWithUndo: FC<AccordionTitleWithUndoProps> = ({
  mappingIsModified,
  title = 'Click to open',
  onUndo
}) => {
  const { treatmentType, dispatch, currentHistoryIndex } = useMappingContext();

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (onUndo) {
      onUndo();
    } else {
      dispatch({ type: 'UNDO_CHANGES', payload: { treatmentType } });
    }
  };

  const isVersionRestored = currentHistoryIndex !== null;
  const showModified = mappingIsModified || isVersionRestored;

  return (
    <Group align="space-between" fullWidth style={{ minHeight: '24px' }} gap="sm">
      <Group.Item style={{ width: isVersionRestored ? '55%' : undefined }}>{title}</Group.Item>
      <Group>
        {isVersionRestored && <Token color="ice">Restoring previous version</Token>}
        {showModified && (
          <Group.Item>
            <Group valign="center" gap="xs">
              <Token color="tangerine">Modified</Token>
              <Button tertiary color="lava" onClick={handleClick}>
                Undo
              </Button>
            </Group>
          </Group.Item>
        )}
      </Group>
    </Group>
  );
};

AccordionTitleWithUndo.displayName = 'AccordionTitleWithUndo';
